import { combineEpics, ofType } from 'redux-observable';
import { map, switchMap, catchError } from 'rxjs/operators';
import { from, of } from 'rxjs';
import { asyncError, asyncFinish } from '../../common/+store/index';
import {
  GET_HB1416_TRACKER,
  UPDATE_HB1416_TRACKER,
  GENERATE_AEP,
  GENERATE_ALL_AEPS,
  MATCH_TIA_DESIGNATIONS,
} from './hb1416Tracker.actions';
import {
  getHb1416Tracker,
  updateHb1416Tracker,
  generateAEP,
  generateAllAEPs,
  matchTiaDesignations,
} from './hb1416Tracker.api';
import { commonApiCallFragment } from '../../common/+store/utils/utils.api';

const getHb1416TrackerEpic = (action$) =>
  action$.pipe(
    ofType(GET_HB1416_TRACKER),
    switchMap((action) =>
      from(getHb1416Tracker(action.snapshotYear)).pipe(commonApiCallFragment(action$, action, 'Get HB 1416 Tracker'))
    ),
    switchMap((result) => {
      if (result.success) {
        const response = result.response || {};
        return of(
          asyncFinish(result.action.type, {
            worklist: response.worklist || response.tracker || {},
          })
        );
      }
      if (result.actions) return of(...result.actions);
      return of(result.action);
    })
  );

const updateHb1416TrackerEpic = (action$) =>
  action$.pipe(
    ofType(UPDATE_HB1416_TRACKER),
    switchMap((action) =>
      from(updateHb1416Tracker(action.snapshotYear, action.firebaseId, action.session, action.fields)).pipe(
        map((data) => ({ action, success: true, data })),
        catchError((error) =>
          of({
            success: false,
            action: asyncError(action.type, 'Could not update HB 1416 tracker', error),
          })
        )
      )
    ),
    switchMap((results) => {
      if (results.success) {
        return of(
          asyncFinish(results.action.type, {
            firebaseId: results.action.firebaseId,
            tracker: results.data && results.data.tracker,
          })
        );
      }
      if (results.actions) return of(...results.actions);
      return of(results.action);
    })
  );

const generateAEPEpic = (action$) =>
  action$.pipe(
    ofType(GENERATE_AEP),
    switchMap((action) =>
      from(generateAEP(action.snapshotYear, action.firebaseId, action.language)).pipe(
        map((data) => ({ action, success: true, data })),
        catchError((error) =>
          of({
            success: false,
            firebaseId: action.firebaseId,
            action: asyncError(action.type, 'Could not generate AEP', error, { firebaseId: action.firebaseId }),
          })
        )
      )
    ),
    switchMap((results) => {
      if (results.success) {
        return of(
          asyncFinish(results.action.type, {
            firebaseId: results.action.firebaseId,
            language: results.action.language,
            pdfUrl: results.data && results.data.pdfUrl,
          })
        );
      }
      if (results.actions) return of(...results.actions);
      return of(results.action);
    })
  );

// Refresh the worklist after a bulk operation lands (the callables change
// tracker/aep/exclusion state for many rows at once). Deliberately no asyncStart:
// a silent background refetch keeps the summary dialog on screen instead of
// swapping the whole page to the loading spinner.
const refreshWorklistActions = (snapshotYear) => [{ type: GET_HB1416_TRACKER, snapshotYear }];

const generateAllAEPsEpic = (action$) =>
  action$.pipe(
    ofType(GENERATE_ALL_AEPS),
    switchMap((action) =>
      from(generateAllAEPs(action.snapshotYear, action.language)).pipe(
        map((data) => ({ action, success: true, data })),
        catchError((error) =>
          of({
            success: false,
            action: asyncError(action.type, 'Could not generate AEPs', error),
          })
        )
      )
    ),
    switchMap((results) => {
      if (results.success) {
        return of(
          asyncFinish(results.action.type, { summary: results.data }),
          ...refreshWorklistActions(results.action.snapshotYear)
        );
      }
      if (results.actions) return of(...results.actions);
      return of(results.action);
    })
  );

const matchTiaDesignationsEpic = (action$) =>
  action$.pipe(
    ofType(MATCH_TIA_DESIGNATIONS),
    switchMap((action) =>
      from(matchTiaDesignations(action.snapshotYear)).pipe(
        map((data) => ({ action, success: true, data })),
        catchError((error) =>
          of({
            success: false,
            action: asyncError(action.type, 'Could not check TIA designations', error),
          })
        )
      )
    ),
    switchMap((results) => {
      if (results.success) {
        return of(
          asyncFinish(results.action.type, { summary: results.data }),
          ...refreshWorklistActions(results.action.snapshotYear)
        );
      }
      if (results.actions) return of(...results.actions);
      return of(results.action);
    })
  );

const epics = combineEpics(
  getHb1416TrackerEpic,
  updateHb1416TrackerEpic,
  generateAEPEpic,
  generateAllAEPsEpic,
  matchTiaDesignationsEpic
);
export default epics;
